import { useState } from 'react';
import { LogIn } from 'lucide-react';
import { useActionLogger } from '../hooks/use-action-logger';
import { QUICK_LOGIN_CREDS } from '../context/user-context';
import { SalonikaTomIcon, TomSalonikaLockup, TomFooter } from './ui/brand-logo';
import { useI18n } from '../context/regional-context';

interface LoginProps {
 onLogin: (username: string, password: string) => void | Promise<void>;
 onQuickLogin?: (userId: string) => void | Promise<void>;
 isDevelopment?: boolean;
}

// Tomamos la lista canónica de usuarios reales desde el contexto.
// Cada uno se sembra en el backend (apps/api/scripts/seed.js).
const DEV_USERS = Object.entries(QUICK_LOGIN_CREDS).map(([id, c]) => ({
 id,
 name: c.label,
 username: c.username,
}));

export function Login({ onLogin, onQuickLogin, isDevelopment = true }: LoginProps) {
 const [username, setUsername] = useState('');
 const [password, setPassword] = useState('');
 const logAction = useActionLogger('auth');
 // Antes de entrar no hay usuario: el idioma sale del último elegido en este navegador.
 const { t } = useI18n();

 const handleSubmit = (e: React.FormEvent) => {
 e.preventDefault();
 logAction.info('LOGIN_ATTEMPT', `Intento de inicio de sesión para usuario: ${username}`, {
 username,
 timestamp: new Date().toISOString(),
 });
 onLogin(username, password);
 };

 const handleQuickLogin = (userId: string, userName: string) => {
 logAction.info('QUICK_LOGIN', `Inicio de sesión rápido: ${userName}`, {
 userId,
 userName,
 timestamp: new Date().toISOString(),
 });
 if (onQuickLogin) {
 onQuickLogin(userId);
 }
 };

 const inputStyle = {
 padding: '14px 16px',
 background: 'var(--surface-2)',
 border: '1.5px solid transparent',
 borderRadius: 'var(--r-md)',
 fontSize: 15,
 fontWeight: 500,
 color: 'var(--ink)',
 fontFamily: 'inherit',
 width: '100%',
 transition: 'background .15s, border-color .15s',
 };

 return (
 <div
 className="min-h-screen flex items-center justify-center p-4 sm:p-6 relative overflow-hidden"
 style={{ background: 'var(--blue-grad)' }}
 >
 {/* Backdrop — círculos decorativos claros sobre el azul de marca */}
 <div
 aria-hidden="true"
 className="absolute inset-0 pointer-events-none"
 style={{
 backgroundImage:
 'radial-gradient(38% 32% at 16% 12%, rgba(255,255,255,0.12), transparent 60%),' +
 'radial-gradient(34% 30% at 88% 92%, rgba(255,255,255,0.07), transparent 60%)',
 }}
 />

 <div className="w-full max-w-md relative animate-apple-slide-up">
 {/* Branding — marca TOM sobre el azul: icono S+TOM, lockup TOM | Salonika */}
 <div className="text-center mb-7 flex flex-col items-center">
 <SalonikaTomIcon size={84} className="mb-5" />
 <h1 className="sk-h1" style={{ margin: 0 }}>
 {/* conTile={false}: el símbolo ya está arriba en 84px, no se repite. */}
 <TomSalonikaLockup size={34} conTile={false} />
 </h1>
 <p
 className="mt-2"
 style={{ color: 'rgba(255,255,255,0.86)', fontSize: 15, fontWeight: 600 }}
 >
 {t('app.subtitulo')}
 </p>
 </div>

 {/* Login Card */}
 <div
 className="p-6 sm:p-8 sk-modal-shell"
 style={{
 background: 'var(--surface)',
 border: '1.5px solid var(--line)',
 boxShadow: 'var(--sh-lg)',
 }}
 >
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <label
 className="block mb-1.5 flex items-center gap-2"
 style={{ color: 'var(--ink-700)', fontSize: 14, fontWeight: 700 }}
 >
 <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--blue-600)' }} />
 {t('login.usuario')}
 </label>
 <input
 type="text"
 value={username}
 onChange={(e) => setUsername(e.target.value)}
 onFocus={(e) => {
 e.currentTarget.style.background = 'var(--surface)';
 e.currentTarget.style.borderColor = 'var(--blue-400)';
 }}
 onBlur={(e) => {
 e.currentTarget.style.background = 'var(--surface-2)';
 e.currentTarget.style.borderColor = 'transparent';
 }}
 style={inputStyle}
 className="focus:outline-none"
 placeholder={t('login.usuarioPlaceholder')}
 required
 autoComplete="username"
 />
 </div>
 <div>
 <label
 className="block mb-1.5 flex items-center gap-2"
 style={{ color: 'var(--ink-700)', fontSize: 14, fontWeight: 700 }}
 >
 <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--blue-600)' }} />
 {t('login.contrasena')}
 </label>
 <input
 type="password"
 value={password}
 onChange={(e) => setPassword(e.target.value)}
 onFocus={(e) => {
 e.currentTarget.style.background = 'var(--surface)';
 e.currentTarget.style.borderColor = 'var(--blue-400)';
 }}
 onBlur={(e) => {
 e.currentTarget.style.background = 'var(--surface-2)';
 e.currentTarget.style.borderColor = 'transparent';
 }}
 style={inputStyle}
 className="focus:outline-none"
 placeholder={t('login.contrasenaPlaceholder')}
 required
 autoComplete="current-password"
 />
 </div>
 <button
 type="submit"
 className="sk-btn sk-btn--primary w-full"
 style={{ minHeight: 48, fontSize: 14.5}}
 >
 <LogIn className="w-[18px] h-[18px]" />
 {t('login.entrar')}
 </button>
 </form>

 {/* Accesos directos de desarrollo eliminados (jun-2026): el login es solo
     usuario + contraseña. */}
 </div>

 {/* Footer — atribución a la marca matriz, enlazada a su web. */}
 <div className="mt-6 flex justify-center">
 <TomFooter className="items-center text-center" />
 </div>
 </div>
 </div>
 );
}